import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { codexApi } from '../api';
import { Mail, MailOpen, ScrollText, Sparkles, Mic, Headphones } from 'lucide-react';
import VoiceRecorder from '../components/VoiceRecorder';
import VoiceNotePlayer from '../components/VoiceNotePlayer';

export default function CodexLetters() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [voiceInbox, setVoiceInbox] = useState([]);
  const [voiceSent, setVoiceSent] = useState([]);
  const [mentor, setMentor] = useState(null);
  const [showRecorder, setShowRecorder] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, vIn, vOut, me] = await Promise.all([
        codexApi.inbox(),
        codexApi.voiceInbox().catch(() => []),
        codexApi.voiceSent().catch(() => []),
        codexApi.me().catch(() => null),
      ]);
      setItems(list);
      setVoiceInbox(vIn);
      setVoiceSent(vOut);
      // Resolve mentor from any received letter or voice note
      const mentorId = vIn[0]?.sender_id || list[0]?.mentor_id;
      const mentorName = vIn[0]?.sender_name || list[0]?.mentor_name;
      if (mentorId) setMentor({ id: mentorId, name: mentorName });
      // Mark unread voice notes as read on view
      for (const n of vIn) {
        if (!n.read) codexApi.voiceMarkRead(n.id).catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const open = async (letter) => {
    setActive(letter);
    if (!letter.read) {
      try {
        await codexApi.markLetterRead(letter.id);
        setItems((arr) => arr.map((l) => l.id === letter.id ? { ...l, read: true } : l));
      } catch (_) { /* swallow */ }
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', opacity: 0.7 }}>Reading the post…</div>;

  const unreadCount = items?.filter((i) => !i.read).length || 0;
  const allVoice = [...voiceInbox.map((v) => ({ ...v, side: 'in' })), ...voiceSent.map((v) => ({ ...v, side: 'out' }))]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 30);

  return (
    <div data-testid="codex-letters" style={{ paddingTop: 8 }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 22 }}>
        <div className="codex-eyebrow">SACRED CORRESPONDENCE</div>
        <h1 className="codex-h1" style={{ marginTop: 6 }}>Letters from your Mentor</h1>
        <p style={{ opacity: 0.75, marginTop: 6 }}>
          {items?.length ? `${items.length} letter${items.length === 1 ? '' : 's'}` : 'No letters yet'}
          {unreadCount > 0 && <span style={{ marginLeft: 8, color: 'var(--codex-gold-light)' }}>· {unreadCount} unread</span>}
          {allVoice.length > 0 && <span style={{ marginLeft: 8, opacity: 0.65 }}>· {allVoice.length} voice note{allVoice.length === 1 ? '' : 's'}</span>}
        </p>
      </motion.div>

      {/* Voice notes section */}
      {(allVoice.length > 0 || mentor) && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Headphones size={16} color="var(--codex-gold-light)" />
              <h2 className="codex-h2" style={{ color: 'var(--codex-gold-light)' }}>Voice Notes</h2>
            </div>
            {mentor && (
              <button
                type="button"
                onClick={() => setShowRecorder((v) => !v)}
                data-testid="codex-letters-voice-reply-toggle"
                className="codex-btn codex-btn-ghost"
                style={{ padding: '6px 12px', fontSize: 11 }}
              >
                <Mic size={12} /> {showRecorder ? 'Cancel' : 'Send voice reply'}
              </button>
            )}
          </div>

          {showRecorder && mentor && (
            <div style={{ marginBottom: 14 }}>
              <VoiceRecorder
                recipientId={mentor.id}
                onSent={() => { setShowRecorder(false); load(); }}
              />
            </div>
          )}

          {allVoice.length > 0 ? (
            <div style={{ display: 'grid', gap: 10 }}>
              {allVoice.map((v) => <VoiceNotePlayer key={v.id} note={v} side={v.side} />)}
            </div>
          ) : (
            <div className="codex-card" style={{ padding: 16, textAlign: 'center', fontSize: 12, opacity: 0.65 }}>
              No voice notes yet — record one above.
            </div>
          )}
        </div>
      )}

      {items?.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="codex-card"
          style={{ padding: 60, textAlign: 'center' }}
        >
          <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} style={{ display: 'inline-block' }}>
            <ScrollText size={56} color="var(--codex-gold-light)" />
          </motion.div>
          <div className="codex-display" style={{ marginTop: 18, fontSize: 22 }}>The post is quiet today.</div>
          <div style={{ opacity: 0.75, marginTop: 6, fontSize: 14 }}>
            When your mentor writes, the seal will appear here.
          </div>
        </motion.div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {items.map((l, i) => (
            <motion.button
              key={l.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.05, 0.4) }}
              whileHover={{ y: -2 }}
              onClick={() => open(l)}
              data-testid={`codex-letter-${l.id}`}
              className={l.read ? 'codex-card' : 'codex-card-gold codex-card'}
              style={{ padding: 18, textAlign: 'left', cursor: 'pointer', color: 'var(--codex-parchment)' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: l.read ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #d4af37, #ea580c)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: l.read ? 'var(--codex-gold-light)' : '#1a0e2c', flexShrink: 0,
                }}>
                  {l.read ? <MailOpen size={18} /> : <Mail size={18} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div className="codex-display" style={{ fontSize: 17, color: 'var(--codex-gold-light)' }}>{l.title}</div>
                    {!l.read && <span style={{ fontSize: 10, color: 'var(--codex-orange)', letterSpacing: '0.2em' }}>NEW</span>}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                    From {l.mentor_name} · {new Date(l.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' })}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {l.body}
                  </div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      )}

      {/* Letter detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(11,10,22,0.88)', backdropFilter: 'blur(8px)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ type: 'spring', damping: 18 }}
              onClick={(e) => e.stopPropagation()}
              className="codex-card-gold codex-card"
              style={{
                padding: 36, maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto',
                background: 'linear-gradient(180deg, #fdf6e3 0%, #f5e9c8 100%)',
                color: '#3a1f0d',
                position: 'relative',
                fontFamily: "'Cinzel', serif",
              }}
              data-testid="codex-letter-detail"
            >
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Sparkles size={20} color="#9a6c1f" />
                <div style={{ fontSize: 11, letterSpacing: '0.4em', color: '#9a6c1f', marginTop: 6 }}>SACRED LETTER</div>
                {active.occasion && <div style={{ fontSize: 11, color: '#9a6c1f', opacity: 0.7, marginTop: 2 }}>· {active.occasion} ·</div>}
              </div>
              <h2 style={{ fontSize: 30, color: '#5b2c0e', textAlign: 'center', marginBottom: 16, fontWeight: 700 }}>{active.title}</h2>
              <p style={{ fontSize: 15, lineHeight: 1.85, fontFamily: 'Georgia, serif', whiteSpace: 'pre-wrap' }}>
                {active.body}
              </p>
              <div style={{ marginTop: 32, textAlign: 'right', fontStyle: 'italic', color: '#5b2c0e' }}>
                — {active.mentor_name},<br />
                <span style={{ fontSize: 12, opacity: 0.7 }}>
                  {new Date(active.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/London' })}
                </span>
              </div>
              <button
                onClick={() => setActive(null)}
                className="codex-btn codex-btn-ghost"
                style={{ marginTop: 28, width: '100%', background: 'rgba(58,31,13,0.08)', borderColor: 'rgba(58,31,13,0.25)', color: '#5b2c0e' }}
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
